import React, { useEffect, useState } from "react";
import styled from "styled-components";
import moment from "moment";

import Thomas from "../../api/Thomas";
import APIRequest from "../../api/APIRequest";
import { ExportOverrideWithTrackLink } from "../../api/LCSModels";

import Color from "../../resources/colors";

import { GenericPopupProps } from "../GenericPopup";
import NoDataComponent from "../NoDataComponent";
import LoadingComponent from "../LoadingComponent";
import { HFlex, VFlex, Arrow, DeleteButton } from "../../pandora/styled";
import { FadeAnimation } from "../../pandora/animations";


const Container = styled(VFlex)<{dimmed: boolean}>`
    margin-top: 30px;
    padding: 15px 20px;
    box-sizing: border-box;

    border-radius: 5px;
    background-color: ${Color.extraDarkGrey};
    opacity: ${props => props.dimmed ? 0.6 : 1};
    transition: opacity 0.3s ease;

    .toggle {
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
    }

    .note {
        margin-top: 5px;
        font-weight: normal;
        color: ${Color.extraLightGrey};
    }
`

const Table = styled.div`
    display: grid;
    grid-template-columns: repeat(6, auto) 1fr auto auto;
    max-height: 400px;
    margin-top: 15px;
    overflow-y: auto;

    animation: ${FadeAnimation} 0.3s ease 1;

    h5 {
        padding: 5px 10px;
        font-weight: normal;
        border-bottom: 1px solid ${Color.lightGrey};
        white-space: nowrap;
    }

    .header {
        position: sticky;
        top: 0;
        font-weight: bold;
        background-color: ${Color.lightGrey};
    }

    .delete {
        display: flex;
        align-items: center;
        padding: 0px 10px;
        border-bottom: 1px solid ${Color.lightGrey};
    }
`


interface ExportOverrideListProps {
    section?: number | null;
    year?: number | null;
    fromDates?: Date[] | null;
    isApplied: boolean;
    setPopup: (popup: GenericPopupProps | null) => void;
}

/**
 * Collapsible list of the registered export overrides that the selected export can apply.
 */
const ExportOverrideList = (props: ExportOverrideListProps): React.ReactElement => {

    const { section, year, fromDates, isApplied, setPopup } = props;
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [overrides, setOverrides] = useState<ExportOverrideWithTrackLink[]>([]);

    const dateParam = (fromDates ?? []).map(d => moment(d).format("YYYY-MM-DD")).join(",");

    useEffect(() => {
        let isCurrent = true;

        const getOverrides = async () => {
            try {
                setIsLoading(true);
                const params = {
                    section: section ?? undefined,
                    year: year ?? undefined,
                    fromDates: dateParam !== "" ? dateParam : undefined
                }
                const res = await Thomas.request(APIRequest.getTrackLinkOverrides, params) as ExportOverrideWithTrackLink[];
                if (isCurrent) setOverrides(res);
            } catch (e) {
                if (isCurrent) setPopup({
                    title: "Could not load export overrides",
                    message: Thomas.getErrorMessage(e),
                    setPopup: setPopup
                })
            } finally {
                if (isCurrent) setIsLoading(false);
            }
        }

        getOverrides();
        return () => { isCurrent = false };
    }, [section, year, dateParam]);

    const deleteOverride = async (override: ExportOverrideWithTrackLink) => {
        try {
            await Thomas.request(APIRequest.deleteTrackLinkOverride, {id: override.id});
            setOverrides(overrides.filter(o => o.id !== override.id));
        } catch (e) {
            setPopup({
                title: "Could not delete Export Override",
                message: Thomas.getErrorMessage(e),
                color: Color.red,
                setPopup: setPopup
            })
        }
    }

    const filters = [
        section != null ? `section ${section}` : null,
        year != null ? `from dates in ${year}` : null,
        dateParam !== "" ? "the selected session dates" : null
    ].filter(f => f !== null);

    return (
        <Container dimmed={!isApplied}>
            <HFlex className="toggle" onClick={() => setIsOpen(!isOpen)}>
                <VFlex>
                    <h4>{`Registered export overrides (${isLoading ? "…" : overrides.length})`}</h4>
                    <h5 className="note">
                        {filters.length > 0 ? `Filtered to ${filters.join(", ")}.` : "All overrides in the database."}
                        {!isApplied && " Override date is off, so these are not applied to this export."}
                    </h5>
                </VFlex>
                <Arrow rotate={isOpen ? 180 : 0} />
            </HFlex>
            {isOpen && isLoading &&
                <LoadingComponent message="Loading export overrides..." />
            }
            {isOpen && !isLoading && overrides.length === 0 &&
                <NoDataComponent message="No export overrides" css="height: 100px;" />
            }
            {isOpen && !isLoading && overrides.length > 0 &&
                <Table>
                    <h5 className="header">Bdl</h5>
                    <h5 className="header">From</h5>
                    <h5 className="header">To</h5>
                    <h5 className="header">Track</h5>
                    <h5 className="header">From date</h5>
                    <h5 className="header">To date</h5>
                    <h5 className="header">Added by</h5>
                    <h5 className="header">Updated</h5>
                    <h5 className="header"></h5>
                    {overrides.map(o =>
                        <React.Fragment key={o.id}>
                            <h5>{o.trackLink.section}</h5>
                            <h5>{`${o.trackLink.fromPlace} ${o.trackLink.fromNode}`}</h5>
                            <h5>{`${o.trackLink.toPlace} ${o.trackLink.toNode}`}</h5>
                            <h5>{o.trackLink.trackNumber || "–"}</h5>
                            <h5>{moment(o.fromDate).format("YYYY-MM-DD")}</h5>
                            <h5>{moment(o.toDate).format("YYYY-MM-DD")}</h5>
                            <h5>{o.user?.name ?? "–"}</h5>
                            <h5>{moment.utc(o.updated).local().format("YYYY-MM-DD HH:mm")}</h5>
                            <div className="delete">
                                <DeleteButton title="Delete export override" onClick={() => deleteOverride(o)} />
                            </div>
                        </React.Fragment>
                    )}
                </Table>
            }
        </Container>
    )
}

export default ExportOverrideList;
